import React, {
  useState, useEffect, useMemo,
} from 'react';
import { parse, format, isValid, subDays } from 'date-fns';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';

import Button from '../../../../shared/_TBComponents/Button';
import DatePicker from '../../../../shared/_TBComponents/DatePicker';
import Input from '../../../../shared/_TBComponents/Input';
import Modal from '../../../../shared/_TBComponents/Modal';
import SelectBox from '../../../../shared/_TBComponents/SelectBox';
import Text from '../../../../shared/_TBComponents/Text';

const FormModalContent = styled.div`
  display: flex;
  flex-direction: column;
  padding: 6px;
  min-width: 100%;
`;

const FormDivider = styled.hr`
  border: none;
  border-top: 1px solid #E8E8E8;
  margin: 10px 0 24px 0;
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 14px;
`;

const CalendarDetailsBox = styled.div`
  margin-top: 4px;
  margin-bottom: 8px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  border-left: 1px solid #BDBDBD;
  padding-left: 16px;
`;

const DateRangeRow = styled.div`
  display: flex;
  gap: 16px;
  align-items: flex-start;
`;

const DatePickerField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const CheckboxGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 20px;
`;

const Checkbox = styled.input`
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
  width: 16px;
  height: 16px;
  border: 1.5px solid #E0E0E0;
  border-radius: 3px;
  background: #FFFFFF;
  position: relative;
  flex-shrink: 0;
  transition: border-color 0.15s;

  &:checked {
    background: #FFFFFF;
    border-color: #77A7FF;
  }

  &:checked::after {
    content: '';
    position: absolute;
    left: 4px;
    top: 1px;
    width: 6px;
    height: 10px;
    border: 2px solid #77A7FF;
    border-top: none;
    border-left: none;
    transform: rotate(45deg);
  }
`;

const CheckboxLabel = styled.div`
  font-family: 'Nunito Sans';
  font-size: 16px;
  color: #444444;
  cursor: pointer;
`;

const InputFieldItem = styled.div`
  display: flex;
  flex-direction: column;

  input:not([disabled]) {
    border-color: #D0D0D0 !important;
    transition: border-color 0.15s;
  }

  input:not([disabled]):focus {
    border-color: ${({ isEditing }) => (isEditing ? '#77A7FF' : '#FFCB47')} !important;
    outline: none !important;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin-bottom: 16px;
`;

const ModalFooter = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
`;

const EXPIRATION_OPTIONS = [
  { id: '1year', name: '1 Year', days: 365 },
  { id: '90days', name: '90 Days', days: 90 },
  { id: '60days', name: '60 Days', days: 60 },
  { id: '45days', name: '45 Days', days: 45 },
  { id: '30days', name: '30 Days', days: 30 },
  { id: '14days', name: '14 Days', days: 14 },
  { id: '7days', name: '7 Days', days: 7 },
  { id: '24hours', name: '24 Hours', days: 1 },
];

const DEFAULT_EXPIRATION = EXPIRATION_OPTIONS.find((option) => option.id === '30days');

// Convert existing code into form state used to populate modal when editing
const buildFormStateFromCode = (code, calendars) => {
  const matchingCal = calendars.find((cal) => cal.id === code.calendar?.id) || null;
  const cal = code.calendar;

  const startDate = cal?.startDate
    ? format(parse(cal.startDate, 'yyyy-MM-dd', new Date()), 'MM/dd/yyyy')
    : '';
  const endDate = cal?.endDate
    ? format(parse(cal.endDate, 'yyyy-MM-dd', new Date()), 'MM/dd/yyyy')
    : '';

  let dateOffset = null;
  if (cal?.offsetDate !== undefined && cal?.offsetDate !== null) {
    dateOffset = cal.offsetDate
      ? format(parse(cal.offsetDate, 'yyyy-MM-dd', new Date()), 'MM/dd/yyyy')
      : '';
  }

  let expirationOption = DEFAULT_EXPIRATION;
  if (code.expirationDate) {
    const expiryDate = parse(code.expirationDate, 'MMMM d, yyyy', new Date());
    if (isValid(expiryDate) && expiryDate >= new Date()) {
      expirationOption = {
        id: 'original',
        name: `${format(expiryDate, 'MMM d, yyyy')} (original)`,
        days: null,
        isDisabled: false,
      };
    }
  }

  return {
    description: code.description || '',
    selectedCalendar: matchingCal,
    selectedGroups: code.groups || [],
    emailNotification: code.emailNotification || false,
    startDate,
    endDate,
    dateOffset,
    expirationOption,
  };
};

const getSelectStyles = (isEditing) => ({
  control: (provided, state) => {
    const focusColor = isEditing ? '#77A7FF' : '#FFCB47';
    return {
      ...provided,
      borderColor: state.isFocused ? focusColor : '#D0D0D0',
      boxShadow: 'none',
      '&:hover': {
        borderColor: state.isFocused ? focusColor : '#D0D0D0',
      },
    };
  },
  option: (provided, state) => {
    let bg = 'white';
    if (state.isFocused) bg = isEditing ? '#EEF3FF' : '#FFFAED';
    return { ...provided, backgroundColor: bg, color: '#333333' };
  },
  menu: (provided) => ({ ...provided, zIndex: 2 }),
});

const EasyJoinModal = ({
  isOpen,
  onClose,
  editingCode,
  groups,
  calendars,
  isCreating,
  isUpdating,
  onCreate,
  onUpdate,
}) => {
  const [description, setDescription] = useState('');
  const [expirationOption, setExpirationOption] = useState(DEFAULT_EXPIRATION);
  const [selectedCalendar, setSelectedCalendar] = useState(null);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [dateOffset, setDateOffset] = useState(null);
  const [emailNotification, setEmailNotification] = useState(false);

  const resetForm = () => {
    setDescription('');
    setExpirationOption(DEFAULT_EXPIRATION);
    setSelectedCalendar(null);
    setSelectedGroups([]);
    setStartDate('');
    setEndDate('');
    setDateOffset(null);
    setEmailNotification(false);
  };

  // Reset form for new codes or populate with existing code data when editing
  useEffect(() => {
    if (!isOpen) return;

    if (!editingCode) {
      resetForm();
      return;
    }

    const state = buildFormStateFromCode(editingCode, calendars);
    setDescription(state.description);
    setSelectedCalendar(state.selectedCalendar);
    setSelectedGroups(state.selectedGroups);
    setEmailNotification(state.emailNotification);
    setStartDate(state.startDate);
    setEndDate(state.endDate);
    setDateOffset(state.dateOffset);
    setExpirationOption(state.expirationOption);
  }, [isOpen, editingCode, calendars]);

  const toApiDate = (dateStr) => (dateStr
    ? format(parse(dateStr, 'MM/dd/yyyy', new Date()), 'yyyy-MM-dd')
    : undefined);

  const buildCalendarPayload = () => (selectedCalendar ? {
    dateStart: toApiDate(startDate),
    dateEnd: toApiDate(endDate),
    dateOffset: dateOffset !== null ? toApiDate(dateOffset) : undefined,
  } : {});

  // Adds original expiry date for code to dropdown options when editing
  const expirationOptions = useMemo(() => {
    if (!editingCode?.expirationDate) return EXPIRATION_OPTIONS;
    const expiryDate = parse(editingCode.expirationDate, 'MMMM d, yyyy', new Date());
    if (!isValid(expiryDate)) return EXPIRATION_OPTIONS;
    return [
      {
        id: 'original',
        name: `${format(expiryDate, 'MMM d, yyyy')} (original)`,
        days: null,
        isDisabled: expiryDate < new Date(),
      },
      ...EXPIRATION_OPTIONS,
    ];
  }, [editingCode?.expirationDate]);

  const handleCreate = () => {
    onCreate({
      description: description || undefined,
      calendar: selectedCalendar?.id || undefined,
      groups: selectedGroups.map((g) => g.id),
      expirationDate: expirationOption?.days || undefined,
      emailNotification: emailNotification ? 1 : 0,
      ...buildCalendarPayload(),
    });
  };

  const handleUpdate = () => {
    onUpdate(editingCode.id, {
      description: description || undefined,
      calendar: selectedCalendar?.id || undefined,
      groups: selectedGroups.map((g) => g.id),
      expirationDate: expirationOption?.days || undefined,
      emailNotification: emailNotification ? 1 : 0,
      ...buildCalendarPayload(),
    });
  };

  const isSubmitting = editingCode ? isUpdating : isCreating;
  const submitLabel = () => {
    if (isSubmitting) return editingCode ? 'Saving...' : 'Generating...';
    return editingCode ? 'Save' : 'Generate Code';
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      customBorderRadius='4px'
      customPadding='16px'
      customMaxWidth='560px'
    >
      <FormModalContent>
        <ModalHeader>
          <Text styledTextProps={{ fontSize: '24px', fontWeight: 900, color: '#444' }}>
            {editingCode ? 'Editing EasyJoin Code' : 'Create an EasyJoin Code'}
          </Text>
          <Button
            icon='remove'
            iconSize={15}
            iconOnly
            onClick={onClose}
            noBorder
            txtColor='#9E9E9E'
          />
        </ModalHeader>

        <Text styledTextProps={{ fontSize: '16px', fontWeight: 700, color: '#444' }} styledWrapperProps={{ marginBottom: '4px' }}>Give it a name</Text>
        <FormField>
          <InputFieldItem isEditing={!!editingCode}>
            <Input
              name='description'
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              width='100%'
              styledPropsInput={{ height: '40px', fontSize: '14px' }}
              placeholder='Ex. “Baseball” or “Intermediate Strength”'
            />
          </InputFieldItem>
          <Text styledTextProps={{ fontSize: '12px', color: '#9E9E9E' }} styledWrapperProps={{ marginTop: '4px' }}>
            The name is for your reference.
            It will also be shown to athletes when they use the code
            to confirm they used the right one.
          </Text>
        </FormField>

        <FormDivider />

        <Text styledTextProps={{ fontSize: '16px', fontWeight: 700, color: '#444' }} styledWrapperProps={{ marginBottom: '4px' }}>When athletes use this code....</Text>
        <FormField style={{ marginTop: '12px' }}>
          <Text styledTextProps={{ fontSize: '16px', color: '#444' }} styledWrapperProps={{ marginBottom: '6px' }}>
            Assign to group (optional)
          </Text>
          <SelectBox
            options={groups}
            value={selectedGroups}
            onChangeFunction={(opts) => setSelectedGroups(opts || [])}
            isMulti
            placeholder='Search for a group'
            height='auto'
            customStyles={getSelectStyles(!!editingCode)}
          />
        </FormField>
        <FormField>
          <Text styledTextProps={{ fontSize: '16px', color: '#444' }} styledWrapperProps={{ marginBottom: '6px' }}>
            Assign to calendar (optional)
          </Text>
          <SelectBox
            options={calendars}
            value={selectedCalendar}
            onChangeFunction={(opt) => {
              setSelectedCalendar(opt);
              if (opt?.id) {
                setStartDate(format(new Date(), 'MM/dd/yyyy'));
              }
            }}
            isClearable
            placeholder='Select a calendar'
            height='auto'
            customStyles={getSelectStyles(!!editingCode)}
          />
        </FormField>

        {selectedCalendar && (
          <CalendarDetailsBox>
            <Text styledTextProps={{ fontSize: '16px', fontWeight: 700, color: '#444' }} styledWrapperProps={{ marginBottom: '4px' }}>
              Calendar assignment details...
            </Text>
            <DateRangeRow>
              <DatePickerField>
                <Text styledTextProps={{ fontSize: '16px', fontWeight: 700, color: '#444' }} styledWrapperProps={{ marginBottom: '6px' }}>Start</Text>
                <DatePicker
                  singleDate
                  startDate={startDate ? parse(startDate, 'MM/dd/yyyy', new Date()) : null}
                  setStartDate={(date) => {
                    setStartDate(date ? format(date, 'MM/dd/yyyy') : '');
                    if (date && dateOffset) {
                      const offset = parse(dateOffset, 'MM/dd/yyyy', new Date());
                      if (offset >= date) setDateOffset('');
                    }
                  }}
                  maxDate={null}
                  placeholderText='Select date'
                  textColor='#333333'
                />
              </DatePickerField>
              <DatePickerField>
                <Text styledTextProps={{ fontSize: '16px', fontWeight: 700, color: '#444' }} styledWrapperProps={{ marginBottom: '6px' }}>
                  End (optional)
                </Text>
                <DatePicker
                  singleDate
                  startDate={endDate ? parse(endDate, 'MM/dd/yyyy', new Date()) : null}
                  setStartDate={(date) => setEndDate(date ? format(date, 'MM/dd/yyyy') : '')}
                  maxDate={null}
                  placeholderText='Select date'
                  textColor='#333333'
                />
              </DatePickerField>
            </DateRangeRow>
            <CheckboxGroup style={{ marginBottom: 0 }}>
              <Checkbox
                type='checkbox'
                id='date-offset'
                checked={dateOffset !== null}
                onChange={(e) => setDateOffset(e.target.checked ? '' : null)}
              />
              <CheckboxLabel htmlFor='date-offset'>
                Enable offset (set the date of the first workout)
              </CheckboxLabel>
            </CheckboxGroup>
            {dateOffset !== null && (
              <DateRangeRow style={{ alignItems: 'center' }}>
                <Text styledTextProps={{ fontSize: '14px', fontWeight: 700, color: '#333333' }} styledWrapperProps={{ marginBottom: '0', whiteSpace: 'nowrap' }}>
                  Date
                </Text>
                <DatePickerField>
                  <DatePicker
                    singleDate
                    startDate={dateOffset ? parse(dateOffset, 'MM/dd/yyyy', new Date()) : null}
                    setStartDate={(date) => setDateOffset(date ? format(date, 'MM/dd/yyyy') : '')}
                    maxDate={startDate ? subDays(parse(startDate, 'MM/dd/yyyy', new Date()), 1) : null}
                    placeholderText='Select date'
                    textColor='#333333'
                  />
                </DatePickerField>
              </DateRangeRow>
            )}
          </CalendarDetailsBox>
        )}

        <FormDivider />

        <Text styledTextProps={{ fontSize: '16px', fontWeight: 700, color: '#444' }}>When should the code expire?</Text>
        <FormField style={{ maxWidth: '260px' }}>
          <SelectBox
            options={expirationOptions}
            value={expirationOption}
            onChangeFunction={(opt) => setExpirationOption(opt)}
            placeholder='Select expiration'
            height='auto'
            customStyles={getSelectStyles(!!editingCode)}
            isClearable={false}
          />
        </FormField>

        <FormDivider />

        <CheckboxGroup>
          <Checkbox
            type='checkbox'
            id='email-notification'
            checked={emailNotification}
            onChange={() => setEmailNotification((prev) => !prev)}
          />
          <CheckboxLabel htmlFor='email-notification'>
            Receive notification by email when new user joins
          </CheckboxLabel>
        </CheckboxGroup>

        <ModalFooter>
          <Button
            name='Cancel'
            fontSize='14px'
            bgColor='#FFF'
            borderColor='#EEE'
            txtColor='#333333'
            customBorderRadius='4px'
            styledTextProps={{ fontWeight: 700 }}
            styledWrapperProps={{ marginRight: '8px' }}
            onClick={onClose}
          />
          <Button
            name={submitLabel()}
            fontSize='14px'
            bgColor='#444'
            txtColor='#FFF'
            borderColor='#444'
            customBorderRadius='4px'
            styledTextProps={{ fontWeight: 700 }}
            disabled={isSubmitting || !description.trim()}
            onClick={editingCode ? handleUpdate : handleCreate}
          />
        </ModalFooter>
      </FormModalContent>
    </Modal>
  );
};

EasyJoinModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  editingCode: PropTypes.shape({
    id: PropTypes.number,
    joinCode: PropTypes.string,
    description: PropTypes.string,
    expirationDate: PropTypes.string,
    emailNotification: PropTypes.bool,
    groups: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.number, name: PropTypes.string })),
    calendar: PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      startDate: PropTypes.string,
      endDate: PropTypes.string,
      offsetDate: PropTypes.string,
    }),
  }),
  groups: PropTypes.arrayOf(PropTypes.shape(
    { id: PropTypes.number, name: PropTypes.string },
  )).isRequired,
  calendars: PropTypes.arrayOf(PropTypes.shape(
    { id: PropTypes.number, name: PropTypes.string },
  )).isRequired,
  isCreating: PropTypes.bool.isRequired,
  isUpdating: PropTypes.bool.isRequired,
  onCreate: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
};

EasyJoinModal.defaultProps = {
  editingCode: null,
};

export default EasyJoinModal;
